import { OnDataOptions } from '@apollo/client/react/types/types.js'
import { isDefined, parseSystemColumnFieldId } from '@doss/common-all'
import { DataType } from '@doss/prisma/types'
import { LinearProgress, Theme } from '@mui/material'
import {
  DataGridPremium,
  DataGridPremiumProps,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridCellEditStopReasons,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridCellSelectionModel,
  GridColumnResizeParams,
  GridPinnedColumns,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  MuiEvent,
  gridClasses,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { get, keyBy, orderBy, uniqBy } from 'lodash'
import { RotateCw } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWindowSize } from 'react-use'
import { Button } from '~/@/components/ui/button'
import { TooltipProvider } from '~/@/components/ui/tooltip'
import {
  ApplicationOrientation,
  CsvInsertedTableRowSubscription,
  DeletedTableColumnSubscription,
  DeletedTableRowSubscription,
  GetDetailedTableDocument,
  GetDetailedTableQuery,
  PushTableColumnSubscription,
  PushTableRowSubscription,
  PushTableTabSubscription,
  TableFilterInput,
  useCsvInsertedTableRowSubscription,
  useDeletedTableColumnSubscription,
  useDeletedTableRowSubscription,
  useGetActiveUsersQuery,
  usePushRowButtonNodesSubscription,
  usePushTableColumnSubscription,
  usePushTableRowSubscription,
  usePushTableTabSubscription,
} from '~/common/generated/apollo-operations'
import { useApplicationOrientation } from '~/common/hooks/useApplicationOrientation'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import useNavigationLoading from '~/common/hooks/useNavigationLoading'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { gray } from '~/common/styles/colors'
import { retrieveTableColumnWidths, storeTableColumnWidth } from '~/common/utils/local-storage'
import {
  TEXT_LENGTH_LIMIT,
  clearColumnWidthCache,
  getGridColDefs,
  getHasChangedFilters,
  getHasChangedSort,
  getNumRows,
  maxColumnWidth,
} from '~/common/utils/table'
import {
  getHeaderCheckboxState,
  getRowCheckboxChecked,
  shouldKeepSelectAll,
} from '~/common/utils/table-selection'
import { RenderErrorBoundary } from '~/components/shared/RenderErrorBoundary'
import { DossColumnLevelMenu } from '~/components/shared/Table/DossColumnLevelMenu'
import { ExportTableDialog } from '~/components/shared/Table/ExportTableDialog'
import { TableEmptyState } from '~/components/shared/Table/TableEmptyState'
import { TableErrorState } from '~/components/shared/Table/TableErrorState'
import { CollapseTableTabNavButton, TableToolbar } from '~/components/shared/Table/TableToolbar'
import { isNumericColumnType, MetricType } from '~/components/tables/aggregation-functions'
import {
  AGGREGATION_ROW_ID,
  AggregationRefreshProvider,
} from '~/components/tables/AggregationPinnedRow'
import { DossTableFooter } from '~/components/tables/DossTableFooter'
import { DossGridFilterModel, filterOperatorToBooleanOperator } from '~/components/tables/filters'
import { useTableActions } from '~/components/tables/hooks/useTableActions'
import { useTableSelection } from '~/components/tables/hooks/useTableSelection'
import {
  CsvTable,
  DetailedTable,
  DetailedTableColumn,
  DetailedTableColumnWithTable,
  DetailedTableRow,
  DetailedTableTab,
  DetailedTableTabColumn,
  DossTableCell,
  DossTableRow,
  TableType,
} from '~/components/tables/types'
import { FormulaPopover } from '../formula/FormulaPopover'
import { ChildTableRecordContextMenu } from '../records/ChildTableRecordContextMenu'
import { NewRecord } from '../records/types'
import { ActiveUser } from '../settings/types'
import { TableColumnFormProvider } from '../table-settings/TableColumnFormProvider'
import { getSystemColumnFilterOptions } from '../tables/table-column'
import { useTableDetails } from '../tables/TableDetailsProvider'
import { ChildTableDetailPanelToggle } from './Table/ChildTableDetailPanelToggle'
import { CollapsePivotTableNavButton } from './Table/CollapsePivotTableNavButton'
import { CustomFilterOrSortPanel } from './Table/CustomFilterOrSortPanel'
import CustomPanel from './Table/CustomPanel'
import { CustomSelectionsCheckbox } from './Table/CustomSelectionsCheckbox'
import { DossTableDetailPanel } from './Table/DossTableDetailPanel'
import { TableBulkUploadDialog } from './Table/TableBulkUploadDialog'
import { TableColumnOption } from './Table/TableColumnAutocomplete'
import { TableMenuOptions, TableMenuTypes, useTableMenuContext } from './Table/TableMenus'
import { TableRowChangeSubscription } from './Table/TableRowChangeSubscription'

const TAB_COUNT_LIMIT = 4
const SELECTION_COL_FIELD = '__selection__'
const HEADER_COL_FIELD = '__header__'
const SELECT_ALL_ROW_CLASSNAME = 'doss-select-all-row'

declare module '@mui/x-data-grid-premium' {
  interface FooterPropsOverrides {
    tableId: string
    parentRowId?: string
    onAddRow: (newRow: NewRecord) => void
    readOnly: boolean
    tableLoading: boolean
    tableColumns: DetailedTableColumn[]
    openTableBulkUploadDialog: boolean
    setOpenTableBulkUploadDialog: (open: boolean) => void
  }

  interface NoRowsOverlayPropsOverrides {
    initialized: boolean
    tableToEdit: CsvTable
    onSuccessfulUpload?: () => void
    openTableBulkUploadDialog: boolean
    setOpenTableBulkUploadDialog: (open: boolean) => void
  }

  interface ColumnMenuPropsOverrides {
    tableColumns: DetailedTableColumn[]
    selectedTableTabColumns: DetailedTableTabColumn[]
    tableType: TableType
  }

  // Has to be called FilterPanelPropsOverrides to match MUI even though it handles filter and sort
  interface FilterPanelPropsOverrides {
    columns: TableColumnOption[]
    table: DetailedTable
    activeUsers: ActiveUser[]
    filterModel: DossGridFilterModel
    onFilterModelChange: (model: DossGridFilterModel) => void
    sortModel: GridSortModel
    onSortModelChange: (model: GridSortModel) => void
    tableId: string
    readOnly: boolean
  }
}

export enum TableSource {
  TABLE_IN_PROJECT_DETAIL = 'TABLE_IN_PROJECT_DETAIL',
  TABLE_DETAIL = 'TABLE_DETAIL',
  CHILD_IN_TABLE_DETAIL = 'CHILD_IN_TABLE_DETAIL',
  CHILD_IN_RECORD_DETAIL = 'CHILD_IN_RECORD_DETAIL',
  TABLE_IN_WORKSPACE_DASHBOARD = 'TABLE_IN_WORKSPACE_DASHBOARD',
  PIVOT_TABLE_DRILLDOWN = 'PIVOT_TABLE_DRILLDOWN',
}

export interface TableProps extends Partial<DataGridPremiumProps> {
  parentRowId?: string
  table: DetailedTable
  tableId: string
  selectedTabId?: string
  setSelectedTabId?: (tabId: string) => void
  showEmptyState: boolean
  tableSource: TableSource
  showPadding: boolean
  showBorderLeft?: boolean
  showBorderTop?: boolean
  cellSelectionModel?: GridCellSelectionModel
  handleCellSelectionModelChange?: (model: GridCellSelectionModel) => void
  showBorderBottom?: boolean
  tableTabNavOpen?: boolean
  setTableTabNavOpen?: (open: boolean) => void
  pivotTableNavOpen?: boolean
  setPivotTableNavOpen?: (open: boolean) => void
  readOnly: boolean
  backlinkColumnId?: string
  tableFilters?: TableFilterInput[] | null
  gridFilterModel?: DossGridFilterModel | null
}

export type DossTableStyles = ReturnType<typeof getStyles>

export function getStyles(
  theme: Theme,
  orientation: ApplicationOrientation = ApplicationOrientation.LEFT_TO_RIGHT,
) {
  return {
    // Styling for datagrid (borders, cell states, sizing, etc.)
    datagrid({
      tableType,
      showBorderBottom,
      showBorderTop = true,
      showBorderLeft = true,
    }: {
      tableType: TableType
      showBorderBottom: boolean
      showBorderTop?: boolean
      showBorderLeft?: boolean
    }) {
      const showBorder =
        tableType === TableType.CHILD_TABLE ||
        tableType === TableType.BACKLINK_TABLE ||
        tableType === TableType.CHART
      return {
        borderRadius: showBorder ? '1px' : '0px',
        borderTop: showBorder && showBorderTop ? 1 : 0,
        borderInlineStart: showBorder && showBorderLeft ? 1 : 0,
        borderInlineEnd: showBorder ? 1 : 0,
        borderBottom: showBorderBottom ? 1 : 0,
        borderColor: theme.palette.common.doss.dataBorder.normal,
        backgroundColor: 'white',
        '& .MuiDataGrid-cell': {
          border: 1,
          borderInlineStart: 0,
          borderTop: 0,
          borderColor: theme.palette.common.doss.dataBorder.light,
          position: 'relative',
          '&.MuiDataGrid-cell--editing': {
            boxShadow: 'none',
          },
        },
        '& .MuiDataGrid-row--lastVisible': {
          borderBottom: 0,
          borderColor: theme.palette.common.doss.dataBorder.light,
        },
        '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
          // pad right margin of last table column to make for easier access to column
          marginInlineEnd: 10,
        },
        '& .MuiDataGrid-pinnedColumns .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
          marginInlineEnd: 0,
        },
        // Conditionally apply this column separator style if the orientation is right to left
        // since we're using the spread operator, it will not appear in left to right mode.
        ...(orientation === ApplicationOrientation.RIGHT_TO_LEFT && {
          '& .MuiDataGrid-columnSeparator': {
            position: 'absolute',
            right: '98.1%',
          },
        }),
        '& .MuiDataGrid-pinnedColumns': {
          borderInlineEnd: 1,
          borderColor: theme.palette.common.doss.dataBorder.normal,
          boxShadow: 'none',
          marginInlineEnd: 1,
        },
        '& .MuiDataGrid-pinnedColumnHeaders': {
          top: 'auto',
          borderInlineEnd: 1,
          borderColor: theme.palette.common.doss.dataBorder.normal,
          boxShadow: 'none',
          marginInlineEnd: 1,
        },
        '& .MuiDataGrid-columnHeader': {
          border: 1,
          borderInlineStart: 0,
          borderTop: 0,
          backgroundColor: theme.palette.common.doss.gray[2],
          borderColor: theme.palette.common.doss.dataBorder.normal,
          // Hide MUI's default filter icon since we have a custom one
          '& .MuiDataGrid-iconButtonContainer': {
            display: 'none',
          },
        },
        '& .MuiDataGrid-columnHeaders': {
          borderRadius: 0,
          borderTop: 1,
          borderBottom: 1,
          backgroundColor: theme.palette.common.doss.gray[2],
          borderColor: theme.palette.common.doss.dataBorder.normal,
        },
        '& .last-column': {
          border: 'none',
          borderBottom: 1,
          borderColor: theme.palette.common.doss.dataBorder.normal,
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: 1,
          borderColor: theme.palette.common.doss.dataBorder.normal,
          paddingInlineEnd: theme.spacing(1.5),
        },
        // Aggregation row styling - handles both regular row and pinned row cases
        // Uses darker background and top border to visually distinguish from data rows
        // Uses !important to override any row background styles
        '& .MuiDataGrid-row[data-id="__aggregation_row__"]': {
          backgroundColor: `${theme.palette.common.doss.gray[2]} !important`,
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${theme.palette.common.doss.dataBorder.normal}`,
            fontWeight: 500,
          },
        },
        '& .MuiDataGrid-pinnedRows--bottom': {
          backgroundColor: 'white !important',
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${theme.palette.common.doss.dataBorder.normal}`,
            fontWeight: 500,
          },
        },
        // For tables without detail panel toggle, remove the margin MUI adds for the toggle column
        '&.no-detail-panel .MuiDataGrid-virtualScrollerRenderZone': {
          marginInlineStart: '0 !important',
        },
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          paddingBlock: theme.spacing(0.25),
          paddingInline: theme.spacing(1),
        },
        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
          paddingBlock: theme.spacing(1),
          paddingInline: theme.spacing(1),
        },
        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
          paddingBlock: theme.spacing(2),
          paddingInline: theme.spacing(1),
        },
        '& .MuiDataGrid-cell:focus-within:not(.detail-panel-toggle-cell):not(.MuiDataGrid-cellCheckbox), & .MuiDataGrid-cell:focus:not(.detail-panel-toggle-cell):not(.MuiDataGrid-cellCheckbox)':
          {
            outline: `1px solid ${theme.palette.common.doss.blue[9]}`,
          },
        '& .detail-panel-toggle-cell:focus-within, & .detail-panel-toggle-cell:focus, & .MuiDataGrid-cellCheckbox:focus-within, & .MuiDataGrid-cellCheckbox:focus':
          {
            outline: 'none !important',
          },
        '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
          outline: 'none !important',
          backgroundColor: theme.palette.common.doss.gray[1],
        },
        [`& .${gridClasses.cell}.Mui-selected:not(.MuiDataGrid-cellCheckbox)`]: {
          backgroundColor: `${theme.palette.common.doss.blue[2]}`,
          '&:hover': {
            backgroundColor: `${theme.palette.common.doss.blue[2]}`,
          },
        },
        // `.Mui-selected` only targets rows in MUI's selection model, so in `selectAll` mode,
        // `doss-select-all-row` will capture the rows not yet loaded.
        [`& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell,
          & .MuiDataGrid-row.${SELECT_ALL_ROW_CLASSNAME} .MuiDataGrid-cell`]: {
          backgroundColor: `${theme.palette.common.doss.blue[2]}`,
          borderBottom: 1,
          borderColor: theme.palette.common.doss.dataBorder.normal,
        },
        '& .detail-panel-toggle-cell': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: '28px !important',
          width: '28px !important',
          margin: '0 !important',
          padding: '0 !important',
          borderInlineEnd: 0,
        },
        '& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
          minWidth: '50px !important',
          maxWidth: '50px !important',
          padding: '0 !important',
          justifyContent: 'center',
          '& .MuiCheckbox-root': {
            padding: '5px',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '16px',
          },
        },
        // Header column styling for child/backlink tables (narrower, no checkbox)
        "& .MuiDataGrid-columnHeader[data-field='__header__'], & .MuiDataGrid-cell[data-field='__header__']":
          {
            padding: '0 !important',
            justifyContent: 'center',
          },
        '& .MuiDataGrid-detailPanel': {
          backgroundColor: theme.palette.common.doss.gray[1],
          overflow: 'visible',
        },
      }
    },

    toggleHeader: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      justifyContent: 'center',
      color: theme.palette.common.doss.gray[7],
    },

    // Styling for the header cells of the table
    headerCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      leftIndent(index?: number) {
        return { marginInlineStart: index === 0 ? theme.spacing(1) : 0 }
      },
      icon: {
        display: 'flex',
        alignItems: 'center',
        color: gray[7],
        marginInlineEnd: 1,
        height: '14px',
        width: '14px',
        withCursor: {
          cursor: 'pointer',
          '&:hover': {
            color: theme.palette.common.doss.blue[9],
          },
        },
      },
      sortIcon: {
        display: 'flex',
        alignItems: 'center',
        color: gray[7],
        marginInlineStart: 1,
      },
      name: {
        display: 'flex',
        alignItems: 'center',
      },
      secondaryName: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.common.doss.gray[8],
      },
    },

    headerDescriptionTitle(hasDescription: boolean) {
      if (hasDescription) {
        return {
          backgroundColor: theme.palette.common.doss.gray[10],
          borderRadius: '2px 2px 0px 0px',
          marginBlock: theme.spacing(0),
          marginInline: theme.spacing(1),
          paddingBlock: theme.spacing(0.5),
          paddingInline: theme.spacing(0.75),
        }
      }
      return null
    },
    headerDescriptionText: {
      paddingTop: theme.spacing(0.5),
      paddingInlineEnd: theme.spacing(0.75),
      paddingBottom: theme.spacing(1),
      paddingInlineStart: theme.spacing(0.75),
    },

    lookupCellIndicator: {
      position: 'absolute',
      right: 5,
      top: 5,
    },

    // Styling for text wrapping of long text components
    textWrapping: {
      whiteSpace: 'normal',
      wordWrap: 'break-word',
    },

    // Styling chips are used for ENUMs / Multi-selects
    enumChip: {
      border: 0.5,
      borderColor: gray[9],
      marginBlock: theme.spacing(0.5),
      marginInline: theme.spacing(0),
    },

    // Styling for numbers to always be right aligned
    number: {
      display: 'flex',
      width: '100%',
      justifyContent: 'flex-end',
    },

    // Container for read only cells so the tooltip will appear even if the cell is empty
    readOnlyCell: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    },
  }
}

// Global throttle storage - lives outside React component lifecycle
const subscriptionThrottles = new Map<
  string,
  {
    lastExecuted: number
    pendingCount: number
    timeoutId?: NodeJS.Timeout
  }
>()

// Union type for all possible subscription data types
type AllSubscriptionData =
  | OnDataOptions<PushTableRowSubscription>
  | OnDataOptions<DeletedTableRowSubscription>
  | OnDataOptions<CsvInsertedTableRowSubscription>
  | OnDataOptions<PushTableColumnSubscription>
  | OnDataOptions<DeletedTableColumnSubscription>

/**
 * Wrapper component for detail panel content that gets the parent grid's viewport width.
 * This follows the MUI DataGrid pattern for preventing detail panel horizontal scroll:
 * https://mui.com/x/react-data-grid/master-detail/#disable-detail-panel-content-scroll
 */
const DetailPanelContentWrapper = ({ children }: { children: React.ReactNode }) => {
  const apiRef = useGridApiContext()
  const dimensions = apiRef.current.getRootDimensions()
  const width = dimensions?.viewportInnerSize.width ?? '100%'

  return (
    <div
      style={{
        position: 'sticky',
        left: 0,
        width,
      }}
    >
      {children}
    </div>
  )
}

/**
 * This is the main table component that is used throughout the application. It renders a DataGrid
 * component with child tables and table contents.
 *
 * The inner component is wrapped in an error boundary: when a query needed to load the table
 * throws (see useTableActions), the boundary renders an error state whose retry remounts the
 * table and re-fires its queries.
 */
export const DossTable = (props: TableProps) => (
  <RenderErrorBoundary
    fallback={(error, reset) => <TableErrorState error={error} onRetry={reset} />}
  >
    <DossTableInner {...props} />
  </RenderErrorBoundary>
)

function DossTableInner({
  table,
  tableId,
  selectedTabId,
  setSelectedTabId,
  parentRowId,
  cellSelectionModel,
  handleCellSelectionModelChange,
  tableSource,
  showEmptyState,
  showBorderLeft = true,
  showBorderTop = true,
  showBorderBottom = true,
  tableTabNavOpen,
  setTableTabNavOpen,
  pivotTableNavOpen,
  setPivotTableNavOpen,
  readOnly,
  backlinkColumnId,
  tableFilters,
  gridFilterModel,
  ...props
}: TableProps) {
  const { isMobile } = useDeviceType()
  const { locale, user } = useGlobalContext()
  const styles = useStyles(getStyles)
  const { permissionsManager } = useGlobalContext()
  const isNavigating = useNavigationLoading()
  const {
    tableType,
    selectedTab,
    tableColumns,
    parentTableColumns,
    childTableColumns,
    tableTabs,
    setHideToolbarButtons,
  } = useTableDetails()
  const sortedTableColumns = useMemo(() => orderBy(tableColumns, 'order', 'asc'), [tableColumns])

  const allColumns = useMemo(() => {
    const sortedParentColumns = orderBy(parentTableColumns, 'order', 'asc')
    // Get the child table columns in order of the child tables
    const childTables = orderBy(table.childTables, (childTable) => childTable.orderOnRecordDetails)
    const sortedChildTableColumns: DetailedTableColumnWithTable[] = []
    for (const childTable of childTables) {
      const sortedColumns = orderBy(
        childTableColumns.filter((column) => column.table.id === childTable.id),
        (column) => column.order,
      )
      sortedChildTableColumns.push(...sortedColumns)
    }
    return [...sortedTableColumns, ...sortedParentColumns, ...sortedChildTableColumns]
  }, [sortedTableColumns, parentTableColumns, childTableColumns, table.childTables])

  const filterPanelColumns = useMemo(
    () => [...allColumns, ...getSystemColumnFilterOptions(table.id, table.name)],
    [allColumns, table.id, table.name],
  )
  const [orderedTableTabs, setOrderedTableTabs] = useState<DetailedTableTab[]>(
    orderBy(tableTabs, 'order', 'asc'),
  )

  const { data: activeUsersData } = useGetActiveUsersQuery()

  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({})
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])
  const [isSelectAll, setIsSelectAll] = useState(false)
  const lastSelectedRowIdRef = useRef<GridRowId | null>(null)
  const proxyInputRef = useRef<HTMLInputElement>(null)

  const { t } = useTypedTranslation()
  const [storedColWidths, setStoredColWidths] = useState<Record<string, number>>({})
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<GridRowId[]>([])

  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [formulaId, setFormulaId] = useState<string | undefined>(undefined)
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number
    mouseY: number
    row: DossTableRow
  } | null>(null)
  const [openTableBulkUploadDialog, setOpenTableBulkUploadDialog] = useState(false)
  // Key to trigger manual re-fetch of aggregation values via the refresh button
  const [aggregationRefreshKey, setAggregationRefreshKey] = useState(0)
  // Track selected metric per column (columnId -> metricType) - lifted from AggregationCell
  // so state persists when columns useMemo re-runs
  const [columnMetrics, setColumnMetrics] = useState<Record<string, MetricType>>({})

  const apiRef = useGridApiRef()

  const { orientation } = useApplicationOrientation()

  const {
    activeMenu,
    selectedOption,
    closeMenu,
    allowInfiniteScroll,
    exportTableId,
    exportBacklinkColumnId,
    openMenu,
  } = useTableMenuContext()

  const handleFilterIconClick = useCallback(
    (columnId: string) => {
      const column = sortedTableColumns.find((col) => col.id === columnId)
      const isSystemColumn = !column && !!parseSystemColumnFieldId(columnId)

      if (column || isSystemColumn) {
        openMenu(
          TableMenuTypes.COLUMN,
          TableMenuOptions.FILTER,
          {
            colDef: {
              field: columnId,
              filterOperators: [],
            },
          },
          apiRef,
        )
      }
    },
    [sortedTableColumns, openMenu, apiRef],
  )

  const parentTableColumnIds = useMemo(() => {
    return parentTableColumns.map((column) => column.id)
  }, [parentTableColumns])

  const {
    // Row data
    rowsData,
    setRowsData,

    // Row count
    rowCount,

    // Filters
    filters,
    draftFilterModel,
    filterModel,
    handleFilterChange,

    // Sorts
    sortModel,
    sorts,
    handleSortChange,

    // Search
    searchString,
    handleSearchChange,

    // Column visibility
    columnVisibilityModel,
    setColumnVisibilityModel,
    savedColumnVisibilityModel,
    onTableTabColumnsSaved,

    // Pagination
    paginationModel,
    handlePaginationChange,

    tableRowsLoading,
    initialLoading,

    resetSelectedTab,
    handleQueryRef,
  } = useTableActions({
    parentRowId,
    tableType,
    tableSource,
    allowInfiniteScroll,
    setHideToolbarButtons,
    apiRef,
    backlinkColumnId,
    tableFilters,
    gridFilterModel,
    currentUserId: user?.id || '',
    parentTableColumnIds,
  })

  // Cleanup throttle state and apollo cache when component unmounts to prevent memory leaks
  // Since subscriptionThrottles is a module-level Map (outside React lifecycle),
  // entries persist indefinitely unless manually removed
  useEffect(() => {
    return () => {
      // Generate the same throttle key used in handleTableSubscription
      const throttleKey = `${tableId}-${parentRowId}`
      const throttleData = subscriptionThrottles.get(throttleKey)

      // Clear any pending timeout to prevent it from executing after component unmounts
      if (throttleData?.timeoutId) {
        clearTimeout(throttleData.timeoutId)
      }

      // Remove this table's throttle entry from the global Map to free memory
      subscriptionThrottles.delete(throttleKey)

      // Clear column width cache for this table type to prevent memory leaks
      clearColumnWidthCache(tableType, tableId)
    }
  }, [tableId, parentRowId, tableType])

  /**
   * Throttles subscription events to prevent excessive table refetches during bulk operations.
   *
   * Without throttling, rapid subscription events (like CSV uploads with 100+ rows) would
   * trigger a table refetch for each event, making the UI unusable. This function ensures
   * refetches happen at most once per throttleMs (default 500ms).
   *
   * Uses a global Map to persist throttle state across component re-renders, keyed by
   * `${tableId}-${parentRowId}` to handle multiple tables independently.
   */
  const handleTableSubscription = useCallback(
    (options: AllSubscriptionData, throttleMs = 1500) => {
      // Only process if subscription contains actual data
      if (options.data.data) {
        // Create unique throttle key for this table instance
        const throttleKey = `${tableId}-${parentRowId}`
        const now = Date.now()

        // Get or create throttle state for this table
        let throttleData = subscriptionThrottles.get(throttleKey)

        if (!throttleData) {
          throttleData = { lastExecuted: 0, pendingCount: 0 }
          subscriptionThrottles.set(throttleKey, throttleData)
        }

        const timeSinceLastExecution = now - throttleData.lastExecuted

        // If enough time has passed since last execution, execute immediately
        if (timeSinceLastExecution >= throttleMs) {
          // Reset throttle state and execute refetch
          throttleData.lastExecuted = now
          throttleData.pendingCount = 0

          // Clear any existing timeout since we're executing now
          if (throttleData.timeoutId) {
            clearTimeout(throttleData.timeoutId)
            throttleData.timeoutId = undefined
          }

          // Refetch table data to reflect subscription changes
          handleQueryRef.current({
            shouldResetRowsData: true,
            forceRefetch: true,
          })
        } else {
          // Too soon to execute - increment pending event counter
          throttleData.pendingCount++

          // Schedule delayed execution only if no timeout is already pending
          // This ensures we don't create multiple timeouts for the same throttle window
          if (!throttleData.timeoutId) {
            // Calculate how much time remains in the current throttle window
            const remainingTime = throttleMs - timeSinceLastExecution

            // Schedule execution for the end of the throttle window
            throttleData.timeoutId = setTimeout(() => {
              const currentThrottleData = subscriptionThrottles.get(throttleKey)
              // Only execute if there are still pending events (component might have unmounted)
              if (currentThrottleData && currentThrottleData.pendingCount > 0) {
                // Reset throttle state and execute refetch
                currentThrottleData.lastExecuted = Date.now()
                currentThrottleData.pendingCount = 0
                currentThrottleData.timeoutId = undefined

                // Refetch table data to reflect all accumulated subscription changes
                handleQueryRef.current({
                  shouldResetRowsData: true,
                  forceRefetch: true,
                })
              }
            }, remainingTime)
          }
        }
      }
    },
    [tableId, parentRowId, handleQueryRef],
  )

  const handlePushTableColumnData = useCallback(
    (options: OnDataOptions<PushTableColumnSubscription>) => {
      handleTableSubscription(options)

      const newColumn = options.data.data?.pushTableColumn
      if (!newColumn) {
        return
      }
      const updatedOrderedTableTabs = orderedTableTabs.map((tab) => {
        const tableTabColumn = newColumn.tableTabs.find(
          (tabColumn) => tabColumn.tableTab.id === tab.id,
        )
        if (tableTabColumn) {
          return {
            ...tab,
            columns: [...tab.columns, { ...tableTabColumn, tableColumn: newColumn }],
          }
        }
        return tab
      })

      const table: GetDetailedTableQuery | null = options.client.cache.readQuery({
        query: GetDetailedTableDocument,
        variables: { tableId },
      })

      if (!table) {
        return
      }

      const newColumns = uniqBy([...table.table.columns, newColumn], (column) => column.id)
      options.client.cache.modify({
        id: `Table:${tableId}`,
        fields: {
          columns: () => newColumns,
          tabs: () => updatedOrderedTableTabs,
        },
      })

      newColumn.tableTabs.map((tableTabColumn) => {
        const updatedTableTab = updatedOrderedTableTabs.find(
          (tab) => tab.id === tableTabColumn.tableTab.id,
        )

        if (!updatedTableTab) {
          return
        }

        options.client.cache.modify({
          id: `TableTab:${tableTabColumn.tableTab.id}`,
          fields: {
            columns: () => updatedTableTab.columns,
          },
        })
      })

      setOrderedTableTabs(updatedOrderedTableTabs)

      const updatedColumnVisibilityModel = { ...columnVisibilityModel }
      updatedColumnVisibilityModel[newColumn.id] = true
      setColumnVisibilityModel(updatedColumnVisibilityModel)
    },
    [
      handleTableSubscription,
      orderedTableTabs,
      tableId,
      columnVisibilityModel,
      setOrderedTableTabs,
      setColumnVisibilityModel,
    ],
  )

  const handlePushTableTabData = useCallback(
    (options: OnDataOptions<PushTableTabSubscription>) => {
      const newTableTab = options.data.data?.pushTableTab
      if (!newTableTab) {
        return
      }

      const queryData: GetDetailedTableQuery | null = options.client.cache.readQuery({
        query: GetDetailedTableDocument,
        variables: { tableId },
      })

      const uniqueOrderedTableTabs = uniqBy([...orderedTableTabs, newTableTab], (tab) => tab.id)
      if (queryData) {
        options.client.cache.writeQuery({
          query: GetDetailedTableDocument,
          variables: { tableId },
          data: {
            table: {
              ...queryData.table,
              tableTabs: uniqueOrderedTableTabs,
            },
          },
        })
      }
    },
    [tableId, orderedTableTabs],
  )

  // Subscribe to row creation/updates - throttled to prevent UI freezing during bulk operations
  usePushTableRowSubscription({
    variables: { input: { tableId, parentRowId } },
    skip: isNavigating,
    onData: (options) => handleTableSubscription(options),
    ignoreResults: true,
  })

  // Subscribe to row deletions - throttled to prevent UI freezing during bulk deletions
  useDeletedTableRowSubscription({
    variables: { input: { tableId, parentRowId } },
    skip: isNavigating,
    onData: (options) => handleTableSubscription(options),
    ignoreResults: true,
  })

  // Subscribe to CSV bulk row insertions - especially important to throttle since CSV uploads
  // can insert hundreds of rows rapidly, each triggering a subscription event
  useCsvInsertedTableRowSubscription({
    variables: { input: { userId: user?.id || '', tableId, parentRowId } },
    skip: !user || isNavigating,
    onData: (options) => handleTableSubscription(options),
    ignoreResults: true,
  })

  // Subscribe to new column additions - throttled to prevent excessive refetches
  // when multiple columns are added in quick succession
  usePushTableColumnSubscription({
    variables: { tableId },
    skip: isNavigating,
    onData: handlePushTableColumnData,
    ignoreResults: true,
  })

  // When a new table tab is created, push it to the front-end
  usePushTableTabSubscription({
    variables: { tableId },
    skip: isNavigating,
    onData: handlePushTableTabData,
    ignoreResults: true,
  })

  // Specialized handler for column deletions that performs immediate UI cleanup
  // before applying standard throttling
  const handleDeletedColumnSubscription = useCallback(
    (options: OnDataOptions<DeletedTableColumnSubscription>) => {
      if (!options.data.data) {
        return
      }

      const deletedColumnId = options.data.data.deletedTableColumn

      // Perform immediate UI updates for column deletion (can't wait for throttled refetch)
      // Remove the deleted column from table tabs immediately to prevent UI inconsistencies
      const updatedOrderedTableTabs = orderedTableTabs.map((tab) => {
        // Filter out the deleted column from each tab's columns
        const updatedColumns = tab.columns.filter(
          (column) => column.tableColumn?.id !== deletedColumnId,
        )
        // Return a new tab object with the updated columns list
        return {
          ...tab,
          columns: updatedColumns,
        }
      })

      setOrderedTableTabs(updatedOrderedTableTabs)

      // Hide the deleted column immediately to prevent render errors
      const updatedColumnVisibilityModel = { ...columnVisibilityModel }
      updatedColumnVisibilityModel[deletedColumnId] = false
      setColumnVisibilityModel(updatedColumnVisibilityModel)

      // Apply throttled refetch for the remaining table data updates
      handleTableSubscription(options)
    },
    [orderedTableTabs, columnVisibilityModel, setColumnVisibilityModel, handleTableSubscription],
  )

  // Subscribe to column deletions - uses specialized handler for immediate UI updates
  // plus throttled refetch for table data
  useDeletedTableColumnSubscription({
    variables: { tableId },
    skip: isNavigating,
    onData: handleDeletedColumnSubscription,
    ignoreResults: true,
  })

  // Subscribe to button node updates for all rows in the table
  usePushRowButtonNodesSubscription({
    variables: { input: { tableId } },
    skip: isNavigating,
    onData: ({ client, data }) => {
      const pushData = data.data?.pushRowButtonNodes
      if (!pushData) {
        return
      }

      // Update the specific row's button processing state in cache
      client.cache.modify({
        id: client.cache.identify({ __typename: 'TableRow', id: pushData.id }),
        fields: {
          processingWorkspaceButton() {
            return pushData.processingWorkspaceButton
          },
          currentButtonNodes() {
            return pushData.currentButtonNodes
          },
        },
      })

      setRowsData((prev) => {
        // Find the row
        const rowIndex = prev.findIndex((row) => row.id === pushData.id)

        if (rowIndex === -1) {
          return prev // Row not loaded yet, skip update (no re-render)
        }

        const currentRow = prev[rowIndex]

        // Check if anything actually changed
        const processingButtonChanged =
          currentRow.processingWorkspaceButton?.id !== pushData.processingWorkspaceButton?.id

        // Check if button nodes changed (same logic as button-node-evaluator)
        // Need to check both directions: nodes added AND nodes removed
        const currentButtonsChanged =
          currentRow.currentButtonNodes.length !== pushData.currentButtonNodes.length ||
          currentRow.currentButtonNodes.some(
            (node) => !pushData.currentButtonNodes.find((n) => n.id === node.id),
          ) ||
          pushData.currentButtonNodes.some(
            (node) => !currentRow.currentButtonNodes.find((n) => n.id === node.id),
          )

        if (!processingButtonChanged && !currentButtonsChanged) {
          return prev // No change, return same reference (no re-render)
        }

        // Create new array with updated row
        const newRows = [...prev]
        newRows[rowIndex] = {
          ...currentRow,
          processingWorkspaceButton: pushData.processingWorkspaceButton,
          currentButtonNodes: pushData.currentButtonNodes, // Also update button nodes!
        }
        return newRows // New reference → re-render only if changed
      })
    },
    ignoreResults: true,
  })

  const { width } = useWindowSize()
  // If the width of the window is less than 1200px, show fewer tabs
  const visibleTableTabs = useMemo(() => {
    switch (true) {
      case width < 1300:
        return 1
      case width < 1500:
        return 2
      case width < 1700:
        return 3
      default:
        return TAB_COUNT_LIMIT
    }
  }, [width])

  // Once the table rows return, set the column widths
  useEffect(() => {
    if (!tableRowsLoading) {
      setStoredColWidths(retrieveTableColumnWidths(tableId))
    }
  }, [tableRowsLoading, tableId])

  // If the table data tabs changes (i.e. caching changes), update the ordered table tabs
  useEffect(() => {
    setOrderedTableTabs(orderBy(table.tableTabs, (tableTab) => tableTab.order))
  }, [setOrderedTableTabs, table.tableTabs])

  const tableSelectionContext = useTableSelection()
  const mergedCellSelectionModel = cellSelectionModel || tableSelectionContext.cellSelectionModel
  const handleMergedCellSelectionModel =
    handleCellSelectionModelChange || tableSelectionContext.handleCellSelectionModelChange
  const deselectAllCells = tableSelectionContext.deselectAllCells

  // Performance optimization: only recompute column widths when transitioning empty↔populated
  const hasRows = rowsData.length > 0

  const computedColWidths = useMemo(() => {
    // Return zero widths if no rows exist yet
    if (!hasRows) {
      return sortedTableColumns.map(() => 0)
    }

    // Computes the default maximum widths to use if the user hasn't re-sized
    const defaultMaxWidths = sortedTableColumns.reduce(
      (maxWidths, column, index) => {
        maxWidths[column.id] = maxColumnWidth(
          index,
          TEXT_LENGTH_LIMIT,
          tableType,
          tableId,
          column.id,
        )
        return maxWidths
      },
      {} as Record<string, number>,
    )

    // For each column, returns the default width
    return sortedTableColumns.map((column) => defaultMaxWidths[column.id])
  }, [sortedTableColumns, tableType, tableId, hasRows])

  // Callback to set the anchorEl for the popover
  const handlePopoverOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchorEl(event.currentTarget)
  }

  // Callback to close the formula popover
  const handlePopoverClose = () => {
    setPopoverAnchorEl(null)
    setFormulaId(undefined)
  }

  // Handler for row selection changes. Keep `isSelectAll` in sync and accurate.
  const handleRowSelectionModelChange = useCallback(
    (newModel: GridRowSelectionModel) => {
      setRowSelectionModel(newModel)
      // MUI types `apiRef.current` as always-defined but it is null until the grid initializes.
      // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const loadedRowIds = apiRef.current ? apiRef.current.getAllRowIds() : []
      setIsSelectAll((wasSelectAll) => shouldKeepSelectAll(wasSelectAll, loadedRowIds, newModel))
    },
    [apiRef],
  )

  const handleClearSelection = useCallback(() => {
    setRowSelectionModel([])
    setIsSelectAll(false)
    lastSelectedRowIdRef.current = null
  }, [])

  // Clear row and cell selection when pagination or tab changes
  useEffect(() => {
    handleClearSelection()
    apiRef.current.unstable_setCellSelectionModel({})
  }, [paginationModel.page, selectedTabId, handleClearSelection, apiRef])

  /**
   * MUI DataGrid pinned columns config that specifies the frozen columns,
   * containing table column IDs of frozen columns sorted ASC by their order.
   * User-frozen columns and the checkbox column (when BIT is enabled) are pinned.
   * The detail panel toggle column is not pinned by default.
   */
  const frozenColumnsConfig: GridPinnedColumns | undefined = useMemo(() => {
    const frozenCols = selectedTab?.columns.filter((col) => col.isFrozen)
    const sortedFrozenCols = orderBy(frozenCols ?? [], (col) => col.tableColumn?.order ?? 0, 'asc')

    // Build the list of pinned column IDs
    const ids = sortedFrozenCols
      .map((col) => col.tableColumn?.id)
      .filter((id): id is string => id != null)

    // Always pin the checkbox/header column to the front of the table
    if (tableType === TableType.TABLE) {
      ids.unshift(SELECTION_COL_FIELD)
    } else if (tableType === TableType.CHILD_TABLE || tableType === TableType.BACKLINK_TABLE) {
      ids.unshift(HEADER_COL_FIELD)
    }

    // Only pin columns if there are any columns to pin
    if (ids.length > 0) {
      if (user?.orientation === ApplicationOrientation.RIGHT_TO_LEFT) {
        return { right: ids }
      } else {
        return { left: ids }
      }
    }

    return { left: [], right: [] }
  }, [selectedTab, user?.orientation, tableType])

  const showDetailPanelToggle =
    table.childTables.length > 0 || table.lookupBacklinkTables.length > 0

  // Show aggregation row if the table has at least one numeric column that can be aggregated.
  const showAggregationRow = sortedTableColumns.some((col) => isNumericColumnType(col.type))

  const columns = useMemo(() => {
    const cols = getGridColDefs({
      tableId,
      tableName: table.name,
      tableColumnHeaders: sortedTableColumns,
      tableType,
      styles,
      maxColWidths: computedColWidths,
      storedColWidths,
      sorts,
      handlePopoverOpen,
      setFormulaId,
      columnVisibilityModel,
      readOnly,
      sortedFrozenColumns: frozenColumnsConfig.left,
      locale,
      activeUsers: activeUsersData?.activeUsers,
      setRowsData,
      permissionsManager,
      t,
      orientation,
      tableSource,
      filterModel: draftFilterModel,
      onFilterIconClick: handleFilterIconClick,
      tableTabId: selectedTab?.id,
      selectedTab: selectedTab ?? undefined,
      showAggregationRow,
      columnMetrics,
      setColumnMetrics,
      // For child tables: use parentRowId; for backlink tables: use backlink params
      parentRowId: backlinkColumnId ? undefined : parentRowId,
      backlinkColumnId,
      backlinkRowId: backlinkColumnId ? parentRowId : undefined,
      searchString,
    })

    if (tableType === TableType.TABLE) {
      cols.unshift({
        field: SELECTION_COL_FIELD,
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        pinnable: false,
        headerAlign: 'center',
        align: 'center',

        renderHeader: () => {
          // Read row ids from MUI's grid state (fed by the `rows` prop derived from rowsData)
          // rather than closing over rowsData — keeps this memo stable across rowsData churn.
          // Race condition on page refresh: renderHeader can be called before MUI DataGrid initializes
          // apiRef.current. This check prevents "Cannot read properties of null".
          // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const loadedRowIds = apiRef.current ? apiRef.current.getAllRowIds() : []
          const disabled = loadedRowIds.length === 0

          const { checked, indeterminate } = getHeaderCheckboxState(
            isSelectAll,
            loadedRowIds,
            rowSelectionModel,
          )

          return (
            <CustomSelectionsCheckbox
              checked={checked}
              disabled={disabled}
              indeterminate={indeterminate}
              // header has no row processing state
              isProcessing={false}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                e.stopPropagation()
                const target = e.target as HTMLInputElement
                const shouldSelect = target.checked

                // Select/deselect all *loaded* rows.
                apiRef.current.selectRows(loadedRowIds, shouldSelect, true)
                // Note that this should come after `selectRows`.
                // The `handleRowSelectionModelChange` invoked by `selectRows` only looks to see
                // whether the checkbox should _remain_ checked.
                setIsSelectAll(shouldSelect)
              }}
            />
          )
        },

        renderCell: (params) => {
          // Show refresh button for aggregation row
          if (params.row.id === AGGREGATION_ROW_ID) {
            return (
              <Button
                className="h-auto shrink-0 px-1.5 py-0.5"
                onClick={() => setAggregationRefreshKey((k) => k + 1)}
                type="button"
                variant="ghost"
              >
                <RotateCw className="size-3.5" />
              </Button>
            )
          }

          const rowId = String(params.id)
          const processingButton = params.row.processingWorkspaceButton
          const checked = getRowCheckboxChecked(
            isSelectAll,
            apiRef.current.isRowSelected(params.id),
          )

          return (
            <CustomSelectionsCheckbox
              key={rowId}
              checked={checked}
              isProcessing={!!processingButton}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                const target = e.target as HTMLInputElement

                if (isSelectAll) {
                  // Update `rowSelectionModel` to include all loaded rows except the clicked one.
                  // This can be removed when we upgrade MUI to v8 and can leverage the exclusion model.
                  setRowSelectionModel(
                    apiRef.current.getAllRowIds().filter((id) => id !== params.id),
                  )
                  setIsSelectAll(false)
                } else if (e.shiftKey && lastSelectedRowIdRef.current !== null) {
                  // Shift-click: Select/deselect range from lastSelectedRowId to current row.
                  // As we use a custom checkbox to show row processing state, we cannot use
                  // MUI's built-in row selection functionality.
                  const isCurrentRowSelected = apiRef.current.isRowSelected(params.id)
                  apiRef.current.selectRowRange(
                    {
                      startId: lastSelectedRowIdRef.current,
                      endId: params.id,
                    },
                    !isCurrentRowSelected, // Toggle based on current row's selection state
                    false, // Don't reset other selections
                  )
                } else {
                  // Normal click: Select single row
                  apiRef.current.selectRow(params.id, target.checked, false)
                }

                // Track this row as the last selected for future range selections
                lastSelectedRowIdRef.current = params.id
              }}
              processingButtonName={processingButton?.name}
              tabIndex={params.tabIndex}
            />
          )
        },
      })
    }

    // Add a header column for child/backlink tables (no checkboxes, just refresh button for aggregation row)
    if (tableType === TableType.CHILD_TABLE || tableType === TableType.BACKLINK_TABLE) {
      cols.unshift({
        field: HEADER_COL_FIELD,
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        pinnable: false,
        headerAlign: 'center',
        align: 'center',
        renderHeader: () => null,
        renderCell: (params) => {
          // Show refresh button for aggregation row
          if (params.row.id === AGGREGATION_ROW_ID) {
            return (
              <Button
                className="h-auto shrink-0 px-1.5 py-0.5"
                onClick={() => setAggregationRefreshKey((k) => k + 1)}
                type="button"
                variant="ghost"
              >
                <RotateCw className="size-3.5" />
              </Button>
            )
          }
          return null
        },
      })
    }

    /**
     * For tables with actual detail panels, use GRID_DETAIL_PANEL_TOGGLE_COL_DEF which
     * MUI handles specially for expand/collapse functionality.
     */
    if (showDetailPanelToggle) {
      cols.unshift({
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        hideable: false,
        headerAlign: 'center',
        headerName: 'Detail Panel Toggle',
        headerClassName: 'detail-panel-toggle-cell',
        cellClassName: 'detail-panel-toggle-cell',
        renderHeader: () => {
          if (tableSource === TableSource.PIVOT_TABLE_DRILLDOWN) {
            return (
              <CollapsePivotTableNavButton
                pivotTableNavOpen={pivotTableNavOpen}
                setPivotTableNavOpen={setPivotTableNavOpen}
              />
            )
          }
          return (
            <CollapseTableTabNavButton
              hideTabButtons={tableSource === TableSource.CHILD_IN_TABLE_DETAIL}
              hideTableExpander={tableSource !== TableSource.TABLE_DETAIL}
              setTableTabNavOpen={setTableTabNavOpen}
              tableTabNavOpen={tableTabNavOpen}
            />
          )
        },
        renderCell: (params) => {
          // Don't show detail panel toggle for aggregation row
          if (params.row.id === AGGREGATION_ROW_ID) {
            return <div />
          }

          return (
            <ChildTableDetailPanelToggle
              id={params.id}
              row={params.row}
              table={table}
              value={params.value}
            />
          )
        },
      })
    }

    return cols
  }, [
    tableId,
    sortedTableColumns,
    tableType,
    styles,
    computedColWidths,
    storedColWidths,
    sorts,
    columnVisibilityModel,
    readOnly,
    frozenColumnsConfig.left,
    locale,
    activeUsersData?.activeUsers,
    setRowsData,
    permissionsManager,
    tableSource,
    draftFilterModel,
    handleFilterIconClick,
    rowSelectionModel,
    isSelectAll,
    showDetailPanelToggle,
    pivotTableNavOpen,
    setPivotTableNavOpen,
    setTableTabNavOpen,
    tableTabNavOpen,
    apiRef,
    orientation,
    t,
    table,
    selectedTab,
    showAggregationRow,
    columnMetrics,
    parentRowId,
    backlinkColumnId,
    searchString,
  ])

  // In `selectAll` mode, tag every row so that our selected-row styling reaches rows not yet in
  // MUI's selection model. Mirrors the checkbox's `isSelectAll` handling.
  // This can be removed when we upgrade MUI to v8 and can leverage the exclusion model instead.
  const getRowClassName = useCallback(
    () => (isSelectAll ? SELECT_ALL_ROW_CLASSNAME : ''),
    [isSelectAll],
  )

  const columnsById = useMemo(() => {
    return keyBy(sortedTableColumns, (column) => column.id)
  }, [sortedTableColumns])

  // Detect if the table has a vertical scrollbar to determine if we should pin the aggregation row
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false)

  // Check for vertical scrollbar after rows change and container resize
  useEffect(() => {
    if (!showAggregationRow) {
      return
    }

    const checkScrollState = () => {
      try {
        const dimensions = apiRef.current.getRootDimensions()
        if (dimensions) {
          // Use the built-in hasScrollY property which indicates if vertical scroll is needed
          setHasVerticalScroll(dimensions.hasScrollY)
        }
      } catch (error) {
        // If we can't determine, default to not pinning
        setHasVerticalScroll(false)
      }
    }

    // Use requestAnimationFrame to check dimensions after grid effects have run
    const rafId = requestAnimationFrame(checkScrollState)

    // Observe container resize to re-check scroll state
    const rootElement = apiRef.current.rootElementRef?.current
    let resizeObserver: ResizeObserver | undefined

    if (rootElement) {
      resizeObserver = new ResizeObserver(() => {
        // Use requestAnimationFrame to ensure we check after the resize has been processed
        requestAnimationFrame(checkScrollState)
      })
      resizeObserver.observe(rootElement)
    }

    return () => {
      cancelAnimationFrame(rafId)
      resizeObserver?.disconnect()
    }
  }, [showAggregationRow, rowsData.length, apiRef])

  const shouldPinAggregation = showAggregationRow && hasVerticalScroll

  const rows = useMemo(() => {
    const dataRows = rowsData.map((row) => {
      return {
        ...row,
        cells: row.cells
          .filter((cell) => isDefined(columnsById[cell.column.id]))
          .map((cell) => ({
            ...cell,
            column: columnsById[cell.column.id],
          })),
      }
    })

    // If few rows and we should show aggregation, add it as a regular row
    // so it appears directly below the last data row without a gap
    if (showAggregationRow && !shouldPinAggregation) {
      // Create a stub row that satisfies DossTableRow's required fields.
      // MUI DataGrid requires rows to have a consistent shape, but we intercept
      // rendering in renderCell so these fields are never actually used.
      const aggregationRow: DossTableRow = {
        __typename: 'TableRow',
        id: AGGREGATION_ROW_ID,
        name: '',
        createdAt: '',
        updatedAt: '',
        hasChildRows: false,
        createdBy: null,
        updatedBy: null,
        rootWorkspace: { __typename: 'Workspace', id: '' },
        currentButtonNodes: [],
        processingWorkspaceButton: null,
        cells: [],
      }
      return [...dataRows, aggregationRow]
    }

    return dataRows
  }, [columnsById, rowsData, showAggregationRow, shouldPinAggregation])

  // Create pinned rows for the aggregation row at the bottom when scrolling is likely
  // This keeps the aggregation row visible/sticky when scrolling through data
  const pinnedRows = useMemo((): { bottom: DossTableRow[] } | undefined => {
    if (!showAggregationRow || !shouldPinAggregation) {
      return undefined
    }
    // Create a stub row that satisfies DossTableRow's required fields.
    const aggregationRow: DossTableRow = {
      __typename: 'TableRow',
      id: AGGREGATION_ROW_ID,
      name: '',
      createdAt: '',
      updatedAt: '',
      hasChildRows: false,
      createdBy: null,
      updatedBy: null,
      rootWorkspace: { __typename: 'Workspace', id: '' },
      currentButtonNodes: [],
      processingWorkspaceButton: null,
      cells: [],
    }
    return {
      bottom: [aggregationRow],
    }
  }, [showAggregationRow, shouldPinAggregation])

  const inlineDisplay = sortedTableColumns.some((column) => column.displayImagesInline)

  const handleColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      // Set local storage with the new width of the column
      const storedWidths = storeTableColumnWidth(tableId, params.colDef.field, params.width)

      // Set the state variable with the new width
      setStoredColWidths(storedWidths)
    },
    [tableId],
  )

  const handleChildRowContextMenu = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (readOnly || !permissionsManager.canDeleteRowFromTable(tableId)) {
        return
      }

      const rowId = (event.currentTarget as HTMLDivElement).getAttribute('data-id')

      // Don't show context menu for aggregation row - it's not a real data row
      if (rowId === AGGREGATION_ROW_ID) {
        return
      }

      event.preventDefault()

      const row = rows.find((row) => row.id === rowId)

      if (!row) {
        return
      }

      // Open the context menu for the child table row
      setContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
        row,
      })
    },
    [readOnly, permissionsManager, tableId, rows],
  )

  // Used to help with editing cells with a single click
  // See https://github.com/mui/mui-x/issues/2186 for more info on editing cells with a single click
  const handleCellClick = useCallback(
    (params: GridCellParams, event: MuiEvent<MouseEvent<HTMLElement>>) => {
      if (!params.isEditable || readOnly) {
        return
      }
      // Only allow single-click to edit on mobile devices
      if (!isMobile) {
        return
      }

      const eventTarget = event.target as HTMLElement
      if (eventTarget.nodeType === 1 && !event.currentTarget.contains(eventTarget)) {
        return
      }

      const cell = params.row.cells.find((c: DossTableCell) => c.column.id === params.field)
      const isTextInputType =
        cell?.column.type === DataType.STRING ||
        cell?.column.type === DataType.NUMBER ||
        cell?.column.type === DataType.ID

      // iOS: focus proxy input synchronously in tap gesture to open keyboard.
      // Then, async focus transfers to the real input via FloatingFocusManager
      if (isTextInputType) {
        proxyInputRef.current?.focus()
      }

      setCellModesModel((prevModel) => {
        return {
          // Revert the mode of the other cells from other rows
          ...Object.keys(prevModel).reduce(
            (acc, id) => ({
              ...acc,
              [id]: Object.keys(prevModel[id]).reduce(
                (acc2, field) => ({
                  ...acc2,
                  [field]: { mode: GridCellModes.View },
                }),
                {},
              ),
            }),
            {},
          ),
          [params.id]: {
            // Revert the mode of other cells in the same row
            ...Object.keys(get(prevModel, params.id, {})).reduce(
              (acc, field) => ({
                ...acc,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
            [params.field]: { mode: GridCellModes.Edit },
          },
        }
      })
    },
    [readOnly, isMobile],
  )

  const handleMenuClose = useCallback(() => {
    // Don't close the menu context if we're currently showing a panel
    if (!selectedOption) {
      closeMenu()
    }
  }, [selectedOption, closeMenu])

  const getDetailPanelContent = useCallback(
    (row: GridRowParams<DetailedTableRow>) => {
      if (!showDetailPanelToggle) {
        return <div></div>
      }
      // Uses DetailPanelContentWrapper which reactively gets the parent grid's viewport width
      // See: https://mui.com/x/react-data-grid/master-detail/#disable-detail-panel-content-scroll
      return (
        <DetailPanelContentWrapper>
          <DossTableDetailPanel
            handleMergedCellSelectionModel={handleMergedCellSelectionModel}
            mergedCellSelectionModel={mergedCellSelectionModel}
            readOnly={readOnly}
            row={row.row}
            table={table}
          />
        </DetailPanelContentWrapper>
      )
    },
    [
      showDetailPanelToggle,
      table,
      handleMergedCellSelectionModel,
      mergedCellSelectionModel,
      readOnly,
    ],
  )

  const hasBacklinkColumns = useMemo(
    () =>
      table.columns
        .flatMap((column) => column.lookupColumnChildren)
        .filter((backlinkColumn) => backlinkColumn.lookupColumnGroup?.showBacklink).length > 0,
    [table.columns],
  )

  const getDetailPanelHeight = useCallback(
    (params: GridRowParams<DossTableRow>) =>
      params.row.hasChildRows || hasBacklinkColumns ? 'auto' : 0,
    [hasBacklinkColumns],
  )

  const handleCellKeyDown = useCallback(
    (_: GridCellParams, e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape') {
        deselectAllCells()
      }
    },
    [deselectAllCells],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => handleSortChange(model, table.name),
    [handleSortChange, table.name],
  )

  const pageSizeOptions = useMemo(() => [getNumRows(tableSource)], [tableSource])

  const handleTabChange = useCallback(
    (tab: DetailedTableTab) => {
      setSelectedTabId?.(tab.id)
    },
    [setSelectedTabId],
  )

  const rowStyle = useMemo(
    () =>
      tableType === TableType.CHILD_TABLE && permissionsManager.canDeleteRowFromTable(tableId)
        ? { cursor: 'context-menu ' }
        : {},
    [tableType, permissionsManager, tableId],
  )

  const handleFilterModelChange = useCallback(
    (model: DossGridFilterModel) => handleFilterChange(model, table.name),
    [handleFilterChange, table.name],
  )

  const handleFilterPanelSortModelChange = useCallback(
    (model: GridSortModel) => handleSortChange(model, table.name),
    [handleSortChange, table.name],
  )

  const datagridSx = useMemo(
    () =>
      styles.datagrid({
        tableType,
        showBorderBottom,
        showBorderLeft,
        showBorderTop,
      }),
    [styles, tableType, showBorderBottom, showBorderLeft, showBorderTop],
  )
  const childInsertParentRowId = tableType === TableType.CHILD_TABLE ? parentRowId : undefined

  const slotProps = useMemo(
    () => ({
      footer: {
        tableId,
        parentRowId: childInsertParentRowId,
        readOnly,
        tableLoading: tableRowsLoading,
        tableColumns,
        openTableBulkUploadDialog,
        setOpenTableBulkUploadDialog,
        totalRowCount: rowCount,
      },
      toolbar: {
        table,
        title: table.name,
        tableSource,
        searchString,
        onSearch: handleSearchChange,
        filterModel,
        sortModel,
        type: tableType,
        orderedTableTabs,
        tableColumns: allColumns,
        selectedTab,
        hideTabButtons: tableTabNavOpen,
        handleTabChange,
        setOrderedTableTabs,
        columnVisibilityModel,
        savedColumnVisibilityModel,
        onTableTabColumnsSaved,
        setTableTabNavOpen,
        handleSortChange,
        resetSelectedTab,
        showBorderTop,
        tabCountLimit: visibleTableTabs,
        readOnly,
        rowSelectionModel,
        isSelectAll,
        totalRowCount: rowCount,
        loadedRowCount: rows.length,
        currentFilters: filters ?? undefined,
        onClearSelection: handleClearSelection,
        setRowsData,
      },
      row: {
        onContextMenu: handleChildRowContextMenu,
        style: rowStyle,
        className: 'group/row',
      },
      noRowsOverlay: {
        initialized: !tableRowsLoading,
        tableToEdit: table,
        openTableBulkUploadDialog,
        setOpenTableBulkUploadDialog,
      },
      columnMenu: {
        tableColumns: sortedTableColumns,
        selectedTableTabColumns: selectedTab?.columns ?? [],
        tableType,
      },
      filterPanel: {
        columns: filterPanelColumns,
        activeUsers: activeUsersData?.activeUsers,
        filterModel: draftFilterModel,
        onFilterModelChange: handleFilterModelChange,
        sortModel,
        onSortModelChange: handleFilterPanelSortModelChange,
        tableId,
        table,
        readOnly,
      },
    }),
    [
      tableId,
      childInsertParentRowId,
      readOnly,
      tableRowsLoading,
      tableColumns,
      openTableBulkUploadDialog,
      setOpenTableBulkUploadDialog,
      table,
      tableSource,
      searchString,
      handleSearchChange,
      filterModel,
      sortModel,
      tableType,
      orderedTableTabs,
      allColumns,
      filterPanelColumns,
      selectedTab,
      tableTabNavOpen,
      handleTabChange,
      setOrderedTableTabs,
      columnVisibilityModel,
      savedColumnVisibilityModel,
      onTableTabColumnsSaved,
      setTableTabNavOpen,
      handleSortChange,
      resetSelectedTab,
      showBorderTop,
      visibleTableTabs,
      rowSelectionModel,
      isSelectAll,
      rowCount,
      rows.length,
      filters,
      handleClearSelection,
      setRowsData,
      handleChildRowContextMenu,
      rowStyle,
      sortedTableColumns,
      draftFilterModel,
      handleFilterModelChange,
      handleFilterPanelSortModelChange,
      activeUsersData?.activeUsers,
    ],
  )

  // If no rows and no empty state, return null
  if (!rowsData.length && !showEmptyState) {
    return null
  }

  return (
    <>
      {/* Hidden proxy input for iOS keyboard: focused synchronously in tap handler
          so the virtual keyboard stays open when focus transfers to the real input */}
      <input
        ref={proxyInputRef}
        aria-hidden
        className="fixed left-0 top-0 size-0 text-base opacity-0"
        tabIndex={-1}
      />
      <TooltipProvider delayDuration={200} skipDelayDuration={100}>
        <TableColumnFormProvider
          columnVisibilityModel={columnVisibilityModel}
          selectedTabId={selectedTabId}
          setColumnVisibilityModel={setColumnVisibilityModel}
          table={table}
        >
          <AggregationRefreshProvider refreshKey={aggregationRefreshKey}>
            <DataGridPremium<DetailedTableRow>
              apiRef={apiRef}
              cellModesModel={cellModesModel}
              className={`group/dtable ${!showDetailPanelToggle ? 'no-detail-panel' : ''}`}
              columnVisibilityModel={columnVisibilityModel}
              columns={columns}
              density={inlineDisplay ? 'comfortable' : 'compact'}
              detailPanelExpandedRowIds={
                showDetailPanelToggle ? detailPanelExpandedRowIds : undefined
              }
              disableRowSelectionOnClick
              filterDebounceMs={500}
              filterMode="server"
              filterModel={draftFilterModel}
              getDetailPanelContent={showDetailPanelToggle ? getDetailPanelContent : undefined}
              getDetailPanelHeight={showDetailPanelToggle ? getDetailPanelHeight : undefined}
              getRowClassName={getRowClassName}
              isCellEditable={(params) => params.row.id !== AGGREGATION_ROW_ID}
              loading={initialLoading}
              onCellClick={handleCellClick}
              onCellEditStop={(params, event) => {
                // Prevent keyboard interactions within the EditTableCellDialog from closing it
                // EditTableCellDialog handles its own closing behavior.
                // When not editing, all default MUI keyboard behaviors still are functional
                if (
                  params.reason === GridCellEditStopReasons.cellFocusOut ||
                  params.reason === GridCellEditStopReasons.tabKeyDown ||
                  params.reason === GridCellEditStopReasons.shiftTabKeyDown
                ) {
                  event.defaultMuiPrevented = true
                }
              }}
              onCellKeyDown={handleCellKeyDown}
              onCellModesModelChange={setCellModesModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
              onColumnWidthChange={handleColumnWidthChange}
              onDetailPanelExpandedRowIdsChange={setDetailPanelExpandedRowIds}
              onMenuClose={handleMenuClose}
              onPaginationModelChange={handlePaginationChange}
              onPreferencePanelClose={closeMenu}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              onSortModelChange={handleSortModelChange}
              pageSizeOptions={pageSizeOptions}
              pagination={true}
              paginationMode="server"
              paginationModel={paginationModel}
              pinnedColumns={frozenColumnsConfig}
              pinnedRows={pinnedRows}
              rowCount={rowCount}
              rowSelectionModel={rowSelectionModel}
              rows={rows}
              slotProps={slotProps}
              slots={{
                toolbar: TableToolbar,
                noRowsOverlay: TableEmptyState,
                columnMenu: DossColumnLevelMenu,
                footer: DossTableFooter,
                filterPanel: CustomFilterOrSortPanel,
                panel: CustomPanel,
                loadingOverlay: LinearProgress,
              }}
              sortModel={sortModel}
              sortingMode="server"
              sx={datagridSx}
              unstable_cellSelection
              unstable_cellSelectionModel={mergedCellSelectionModel}
              unstable_onCellSelectionModelChange={handleMergedCellSelectionModel}
              {...props}
              // Setting clipboardExport: false so copy uses the column's `valueFormatter` instead
              // of MUI's default formatter (which adds commas to numbers)
              unstable_ignoreValueFormatterDuringExport={{
                clipboardExport: false,
                csvExport: false,
              }}
            />
          </AggregationRefreshProvider>
        </TableColumnFormProvider>
        {activeMenu === TableMenuTypes.TABLE &&
          exportTableId &&
          selectedOption === TableMenuOptions.EXPORT && (
            <ExportTableDialog
              activeUsers={activeUsersData?.activeUsers}
              backlinkColumnId={exportBacklinkColumnId ?? backlinkColumnId}
              backlinkRowId={backlinkColumnId ? parentRowId : undefined}
              currentColumnVisibilityModel={columnVisibilityModel}
              currentFilterBooleanOperator={filterOperatorToBooleanOperator(
                filterModel.logicOperator,
              )}
              currentFilters={filters ?? undefined}
              currentSorts={sorts ?? undefined}
              exportTableId={exportTableId}
              hasUnsavedFilters={
                selectedTab
                  ? getHasChangedFilters(
                      allColumns,
                      selectedTab,
                      filterModel,
                      tableId,
                      permissionsManager,
                    )
                  : false
              }
              hasUnsavedSorts={selectedTab ? getHasChangedSort(selectedTab, sortModel) : false}
              parentRowId={backlinkColumnId ? undefined : parentRowId}
              rowCount={rowCount}
              selectedTableTab={selectedTab}
              tableType={tableType}
              topLevelTable={table}
            />
          )}
        {contextMenu && (
          <ChildTableRecordContextMenu
            contextMenu={contextMenu}
            setContextMenu={setContextMenu}
            table={table}
          />
        )}
        {formulaId && popoverAnchorEl && (
          <FormulaPopover
            align="top"
            anchorEl={popoverAnchorEl}
            formulaId={formulaId}
            onClose={handlePopoverClose}
            tableId={tableId}
          />
        )}
        {/* Add subscriptions for each table row for any changes */}
        {rowsData.map((row, rowIndex) => {
          return (
            <TableRowChangeSubscription
              key={row.id}
              rowIndex={rowIndex}
              setRowsData={setRowsData}
              tableRowId={row.id}
            />
          )
        })}
        <TableBulkUploadDialog
          onClose={() => {
            setOpenTableBulkUploadDialog(false)
          }}
          open={openTableBulkUploadDialog}
          parentRowId={childInsertParentRowId}
          table={{ ...table, childTables: [] }}
        />
      </TooltipProvider>
    </>
  )
}
